import { useState } from 'react';

// Componente de presentación: recibe los datos ya resueltos.
//
// Antes llamaba a useNotificaciones por su cuenta, y eso rompía el tiempo real. Este
// componente devuelve null cuando no hay nada pendiente, así que con cero
// notificaciones no existía: el hook no corría, el socket no tenía listeners
// registrados, y el primer aviso de la sesión se perdía. Solo aparecía al recargar la
// página (que es cuando el contador ya venía en 1). Ahora el hook vive en
// NotificacionesGlobal, que sí está siempre montado.
const NotificacionesFlotante = ({
	notificaciones = [],
	notificacionesNoLeidas = 0,
	marcarComoLeida,
	marcarTodasComoLeidas,
	onNavigate,
}) => {
	const [mostrarDetalle, setMostrarDetalle] = useState(false);

	// Manejar click en notificación: marcar como leída y navegar
	const handleNotificacionClick = async (notificacion) => {
		console.log('🔍 NotificacionesFlotante Debug - Click en notificación:', notificacion);
		console.log('  - notificacionesNoLeidas ANTES:', notificacionesNoLeidas);

		try {
			// Marcar como leída
			console.log('🔍 Llamando a marcarComoLeida con ID:', notificacion.id);
			await marcarComoLeida(notificacion.id, notificacion.id_partido_obra);
			console.log('✅ marcarComoLeida completado');

			// Navegar a la obra de la notificación. Se pasa el objeto entero y no el
			// nombre: para abrir la obra hay que ir por id_partido_obra, el nombre no
			// identifica nada (y ni siquiera viene siempre).
			if (onNavigate) {
				console.log('🔍 Navegando a obra:', notificacion.id_partido_obra);
				onNavigate(notificacion);
			}

			// Cerrar el detalle después de procesar
			setMostrarDetalle(false);
			console.log('🔍 Detalle cerrado');
		} catch (error) {
			console.error('❌ Error procesando notificación:', error);
		}
	};

	// Limpiar el aviso SIN abrir la obra.
	//
	// El clic sobre la fila siempre lleva a la edición de la obra, que es lo que
	// quiere el seguimiento. Pero no todos los avisos se quieren resolver ahora: a
	// veces hay 40 avisos acumulados y lo que se quiere es sacarlos de la pantalla.
	// Por eso la fila lleva un botón aparte, que marca como leída y nada más.
	//
	// El stopPropagation es lo que evita que el clic llegue al onClick de la fila: sin
	// esto, el botón limpiaba la notificación y acto seguido abría la obra, que es
	// justo lo contrario de lo que se pidió.
	const handleLimpiar = async (event, notificacion) => {
		event.stopPropagation();
		try {
			await marcarComoLeida(notificacion.id, notificacion.id_partido_obra);
		} catch (error) {
			console.error('❌ Error limpiando notificación:', error);
		}
	};

	const handleLimpiarTodas = async () => {
		if (marcarTodasComoLeidas) {
			await marcarTodasComoLeidas();
		}
	};

	const formatearFecha = (timestamp) => {
		const fecha = new Date(timestamp);
		const ahora = new Date();
		const diffMs = ahora - fecha;
		const diffMins = Math.floor(diffMs / 60000);

		if (diffMins < 1) return 'Ahora';
		if (diffMins < 60) return `Hace ${diffMins} min`;
		if (diffMins < 1440) return `Hace ${Math.floor(diffMins / 60)} hs - ${fecha.toLocaleDateString()}`;
		return fecha.toLocaleDateString();
	};

	// Si no hay notificaciones pendientes, no mostrar
	if (notificacionesNoLeidas === 0) {
		return null;
	}

	return (
		<div className="fixed top-4 right-4 z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 min-w-80">
			{/* Header con contador */}
			<div
				className="flex items-center justify-between cursor-pointer mb-3"
				onClick={() => setMostrarDetalle(!mostrarDetalle)}
			>
				<div className="flex items-center space-x-2">
					<div className="w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
						<span className="text-white text-xs font-bold">
							{notificacionesNoLeidas}
						</span>
					</div>
					<span className="text-sm font-medium text-gray-700">
						{notificacionesNoLeidas === 1 ? 'Notificación pendiente' : `${notificacionesNoLeidas} notificaciones pendientes`}
					</span>
				</div>

				{/* Limpiar todas, sin abrir ninguna obra */}
				{notificacionesNoLeidas > 1 && (
					<button
						type="button"
						title="Marcar todas como leídas sin abrir las obras"
						className="text-xs text-gray-500 hover:text-red-600 font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
						onClick={(e) => { e.stopPropagation(); handleLimpiarTodas(); }}
					>
						Limpiar todas
					</button>
				)}
				<svg
					className={`w-4 h-4 text-gray-500 transform transition-transform ${mostrarDetalle ? 'rotate-180' : ''}`}
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7 7" />
				</svg>
			</div>

			{/* Detalle de notificaciones */}
			{mostrarDetalle && (
				<div className="border-t border-gray-200 pt-3 mt-3">
					<div className="max-h-96 overflow-y-auto">
						{notificaciones
							.filter(n => !n.leida)
							.slice(0, 10) // Máximo 10 notificaciones visibles
							.map((notificacion) => (
								<div
									key={notificacion.id}
									className="p-3 hover:bg-gray-50 border-b border-gray-100 cursor-pointer transition-colors"
									onClick={() => handleNotificacionClick(notificacion)}
								>
									<div className="flex items-start space-x-3">
										{/* Icono según tipo */}
										<div className="flex-shrink-0">
											{notificacion.tipo === 'pago_realizado' && (
												<div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
													<span className="text-green-600 text-sm">💰</span>
												</div>
											)}
											{notificacion.tipo === 'certificacion_cargada' && (
												<div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
													<span className="text-blue-600 text-sm">📋</span>
												</div>
											)}
										</div>

										{/* Contenido */}
										<div className="flex-1 min-w-0">
											<div className="text-sm font-medium text-gray-900">
												{notificacion.titulo}
											</div>
											<div className="text-sm text-gray-600 mt-1">
												{notificacion.mensaje}
											</div>
											<div className="text-xs text-gray-400 mt-2">
												{formatearFecha(notificacion.fecha_creacion)}
											</div>
										</div>

										{/* Limpiar el aviso sin abrir la obra.
										    Siempre visible y no solo al pasar el mouse: en touch
										    no hay forma de alcanzarlo. */}
										<button
											type="button"
											title="Limpiar sin abrir la obra"
											aria-label="Limpiar notificación sin abrir la obra"
											className="flex-shrink-0 w-6 h-6 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors"
											onClick={(e) => handleLimpiar(e, notificacion)}
										>
											<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
											</svg>
										</button>
									</div>
								</div>
							))}
					</div>

					{/* Botón para ver todas */}
					{notificaciones.filter(n => !n.leida).length > 10 && (
						<div className="p-3 border-t border-gray-200">
							<button className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium">
								Ver todas las notificaciones ({notificaciones.filter(n => !n.leida).length})
							</button>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default NotificacionesFlotante;
